import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { FormControlDefinition } from 'src/app/model/FormControlDefinition';
import { PasswordChangeEntry } from 'src/app/model/PasswordChangeEntry';
import { PasswordRequestRecord } from 'src/app/model/PasswordRequestRecord';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PasswordRequestService } from 'src/app/services/password-request/password-request.service';
import { UserService } from 'src/app/services/user/user.service';
import { confirmPasswordMatchValidator, customPasswordValidator } from 'src/app/shared/password.validator';

/**
 * Change password page component
 */
@Component({
    selector: 'app-change-password-page',
    templateUrl: './change-password-page.component.html',
    styleUrl: './change-password-page.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChangePasswordPageComponent {
  /** Injected dependencies */
  private _userService: UserService = inject(UserService);
  private _router: Router = inject(Router);
  private _route: ActivatedRoute = inject(ActivatedRoute);
  private _passwordRequestService: PasswordRequestService = inject(PasswordRequestService);
  private _authService: AuthService = inject(AuthService);
  private _toastrService: ToastrService = inject(ToastrService);

  /** Password reset form group */
  public resetForm: FormGroup = new FormGroup({
    email: new FormControl("", [Validators.required, Validators.email]),
    password: new FormControl("", [customPasswordValidator]),
    confirmPassword: new FormControl("", [Validators.required, confirmPasswordMatchValidator("password")]),
  });

  /** Password Request record object */
  public requestRecord: PasswordRequestRecord | null = null;

  /** Status flags */
  public isLoading: boolean = true;
  public isSubmitting: boolean = false;

  /** Error message to display if any */
  public errorMessage: string = "";

  /**
   * When the component is first initalized, check if they're authenticated and if their input is valid
   */
  ngOnInit(): void {
    // Move to applications if already authenticated
    if (this._authService.authenticated) this._router.navigate([this._userService.defaultNavPath]);

    // Find the GUID from the URL
    this._route.paramMap.subscribe(params => {
      const guid = params.get("guid") || "";

      if (guid === "") {
        console.log("[INFO]: password request guid is empty, navigating to login");
        this._router.navigate([AppConstants.MENU_LOGIN]);
        return;
      }

      // Get the entry associated with the provided GUID
      this._passwordRequestService.getPasswordRequestRecord(guid).pipe(
        finalize(() => this.isLoading = false),
      ).subscribe({
        next: (record: PasswordRequestRecord | null): void => {
          // If no record is found, navigate to login
          if (record === null) {
            this._toastrService.error("Invalid or expired password reset link. Please request a new link.", "Error");
            return;
          }

          // Update the record
          this.requestRecord = record;
          this.resetForm.controls["email"].setValue(record.email);
        },
        error: (error: HttpErrorResponse): void => {
          console.error("[CHANGE-PASSWORD-COMPONENT]: Error fetching password request record", error);
          this._toastrService.error("An error occurred while processing your request. Please try again later.", "Error");
          this.errorMessage = error.error.message || "An error occurred while processing your request. Please try again later.";
        },
      });
    });
  }

  /**
   * Move to the applications view if already authenticated
   */
  ngAfterViewInit(): void {
    if (this._authService.authenticated) this._router.navigate([this._userService.defaultNavPath]);
  }

  /**
   * Change a user's password
   */
  public changePassword(): void {
    // If the request record is null, don't log in
    if (this.requestRecord == undefined) return;

    // If the user clicked the change password button, don't let them click again
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    // Create a password change entry
    const changeEntry: PasswordChangeEntry = {
      email: this.resetForm.value.email,
      password: this.resetForm.value.password,
      token: this.requestRecord.token,
    };

    this._passwordRequestService.changePassword(changeEntry).pipe(
      finalize(() => setTimeout(() => this.isSubmitting = false, 500))
    ).subscribe({
      next: (): void => {
        this.errorMessage = "";
        this._toastrService.success("Your password has been changed successfully!");
        this._router.navigate([AppConstants.MENU_LOGIN]);
      },
      error: (error: HttpErrorResponse): void => {
        console.error("[CHANGE-PASSWORD-COMPONENT]: Error changing password", error);

        this.errorMessage = error.error?.message || "An unexpected error occurred. Please try again later.";
        this._toastrService.error("An error occurred while changing your password. Please try again later.", "Error");
      },
    });
  }
}
